<template>
  <div class="chat-page">
    <!-- Sidebar - Conversations List -->
    <aside class="sidebar" :class="{ 'open': sidebarOpen }">
      <div class="sidebar-header">
        <h2>Conversations</h2>
        <button @click="startNewConversation" class="new-chat-btn">
          + New Chat
        </button>
      </div>
      
      <div class="conversations-list">
        <div 
          v-for="conv in conversations" 
          :key="conv.id"
          class="conversation-item"
          :class="{ 'active': currentConversation?.id === conv.id }"
          @click="selectConversation(conv)"
        >
          <div class="conv-info">
            <span class="conv-title">{{ conv.title || 'New Conversation' }}</span>
            <span class="conv-date">{{ formatDate(conv.updated_at) }}</span>
          </div>
          <button @click.stop="confirmDelete(conv)" class="delete-btn">🗑️</button>
        </div>
        
        <div v-if="conversations.length === 0" class="no-conversations">
          <p>No conversations yet</p>
          <p class="hint">Start a new chat to ask medical questions</p>
        </div>
      </div>
      
      <div class="sidebar-footer">
        <div class="credits-display">
          <span class="credits-icon">💳</span>
          <span>{{ credits }} credits remaining</span>
        </div>
      </div>
    </aside>

    <!-- Mobile Sidebar Toggle -->
    <button @click="sidebarOpen = !sidebarOpen" class="sidebar-toggle">
      {{ sidebarOpen ? '✕' : '☰' }}
    </button>

    <!-- Main Chat Area -->
    <main class="chat-main">
      <!-- Header -->
      <header class="chat-header">
        <div class="header-info">
          <h1>{{ currentConversation?.title || 'Medical Information Assistant' }}</h1>
          <span class="header-subtitle">For Healthcare Providers</span>
        </div>
        <div class="header-actions">
          <span class="message-count" v-if="currentConversation">
            {{ currentConversation.message_count }}/10 messages
          </span>
        </div>
      </header>

      <!-- Messages Area -->
      <div class="messages-container" ref="messagesContainer">
        <!-- Welcome Message -->
        <div v-if="!currentConversation" class="welcome-message">
          <div class="welcome-icon">🏥</div>
          <h2>Welcome to Medical Information Assistant</h2>
          <p>Get general medical information to support your practice. Ask about conditions, treatments, procedures, or medical concepts.</p>
          <div class="welcome-disclaimer">
            <strong>ℹ️ Important:</strong> This provides general medical information only, not clinical advice for specific patients. Always use your professional judgment.
          </div>
          <button @click="startNewConversation" class="start-btn">Start New Conversation</button>
        </div>

        <!-- Messages -->
        <div v-else class="messages-list">
          <div 
            v-for="msg in messages" 
            :key="msg.id"
            class="message"
            :class="msg.role"
          >
            <div class="message-avatar">
              {{ msg.role === 'user' ? '👨‍⚕️' : '🤖' }}
            </div>
            <div class="message-content">
              <div class="message-text" v-html="formatMessage(msg.content)"></div>
              <span class="message-time">{{ formatTime(msg.created_at) }}</span>
            </div>
          </div>
          
          <!-- Loading indicator -->
          <div v-if="isLoading" class="message assistant">
            <div class="message-avatar">🤖</div>
            <div class="message-content">
              <div class="typing-indicator">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Input Area -->
      <div class="input-area" v-if="currentConversation">
        <div v-if="currentConversation.message_count >= 10" class="limit-warning">
          <p>This conversation has reached the 10 message limit. Please start a new conversation.</p>
          <button @click="startNewConversation" class="new-chat-btn">Start New Chat</button>
        </div>
        
        <form v-else @submit.prevent="sendMessage" class="input-form">
          <textarea
            v-model="inputMessage"
            placeholder="Ask a medical question..."
            rows="2"
            @keydown.enter.exact.prevent="sendMessage"
            :disabled="isLoading || credits <= 0"
          ></textarea>
          <button type="submit" :disabled="!inputMessage.trim() || isLoading || credits <= 0">
            {{ isLoading ? '...' : 'Send' }}
          </button>
        </form>
        
        <div v-if="credits <= 0" class="no-credits-warning">
          <p>You're out of credits. <router-link to="/credits">Purchase more</router-link> to continue.</p>
        </div>
      </div>
    </main>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click="showDeleteModal = false">
      <div class="modal" @click.stop>
        <h3>Delete Conversation?</h3>
        <p>This action cannot be undone.</p>
        <div class="modal-actions">
          <button @click="showDeleteModal = false" class="cancel-btn">Cancel</button>
          <button @click="deleteConversationConfirmed" class="delete-confirm-btn">Delete</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'
import { authToken, isAuthenticated } from '@/services/auth'
import { 
  getCreditsInfo, 
  createConversation, 
  listConversations, 
  getConversation, 
  sendMessage as sendChatMessage,
  deleteConversation 
} from '@/services/chat'

const router = useRouter()

// State
const sidebarOpen = ref(false)
const conversations = ref([])
const currentConversation = ref(null)
const messages = ref([])
const inputMessage = ref('')
const isLoading = ref(false)
const credits = ref(0)
const showDeleteModal = ref(false)
const conversationToDelete = ref(null)
const messagesContainer = ref(null)

// Load initial data
onMounted(async () => {
  await loadCredits()
  await loadConversations()
})

// Auto-scroll to bottom when messages change
watch(messages, () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}, { deep: true })

async function loadCredits() {
  try {
    const data = await getCreditsInfo(authToken.value)
    credits.value = data.credits
  } catch (error) {
    console.error('Failed to load credits:', error)
  }
}

async function loadConversations() {
  try {
    const data = await listConversations(authToken.value, 1, 50)
    conversations.value = data.conversations || []
  } catch (error) {
    console.error('Failed to load conversations:', error)
  }
}

async function startNewConversation() {
  try {
    isLoading.value = true
    const conv = await createConversation(authToken.value, null, 'english')
    conversations.value.unshift(conv)
    await selectConversation(conv)
    sidebarOpen.value = false
  } catch (error) {
    console.error('Failed to create conversation:', error)
    alert('Failed to create conversation: ' + error.message)
  } finally {
    isLoading.value = false
  }
}

async function selectConversation(conv) {
  try {
    isLoading.value = true
    const data = await getConversation(authToken.value, conv.id)
    currentConversation.value = data.conversation
    messages.value = data.messages || []
    sidebarOpen.value = false
  } catch (error) {
    console.error('Failed to load conversation:', error)
  } finally {
    isLoading.value = false
  }
}

async function sendMessage() {
  if (!inputMessage.value.trim() || isLoading.value || !currentConversation.value) return
  
  const userMessage = inputMessage.value.trim()
  inputMessage.value = ''
  
  // Add user message to UI immediately
  messages.value.push({
    id: 'temp-' + Date.now(),
    role: 'user',
    content: userMessage,
    created_at: new Date().toISOString()
  })
  
  try {
    isLoading.value = true
    const response = await sendChatMessage(authToken.value, currentConversation.value.id, userMessage)
    
    // Add assistant response
    messages.value.push(response.message)
    credits.value = response.credits_remaining
    
    // Update conversation message count
    currentConversation.value.message_count += 2
    
    // Update title if it's the first message
    if (currentConversation.value.message_count === 2 && !currentConversation.value.title) {
      currentConversation.value.title = userMessage.substring(0, 50) + (userMessage.length > 50 ? '...' : '')
      // Update in sidebar list
      const idx = conversations.value.findIndex(c => c.id === currentConversation.value.id)
      if (idx !== -1) {
        conversations.value[idx].title = currentConversation.value.title
      }
    }
  } catch (error) {
    console.error('Failed to send message:', error)
    // Remove the temp user message on error
    messages.value.pop()
    alert('Failed to send message: ' + error.message)
  } finally {
    isLoading.value = false
  }
}

function confirmDelete(conv) {
  conversationToDelete.value = conv
  showDeleteModal.value = true
}

async function deleteConversationConfirmed() {
  if (!conversationToDelete.value) return
  
  try {
    await deleteConversation(authToken.value, conversationToDelete.value.id)
    conversations.value = conversations.value.filter(c => c.id !== conversationToDelete.value.id)
    
    if (currentConversation.value?.id === conversationToDelete.value.id) {
      currentConversation.value = null
      messages.value = []
    }
  } catch (error) {
    console.error('Failed to delete conversation:', error)
    alert('Failed to delete conversation: ' + error.message)
  } finally {
    showDeleteModal.value = false
    conversationToDelete.value = null
  }
}

function formatMessage(content) {
  // Convert newlines to <br> and basic markdown
  return content
    .replace(/\n/g, '<br>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
}

function formatDate(dateStr) {
  const date = new Date(dateStr)
  const now = new Date()
  const diff = now - date
  
  if (diff < 86400000) { // Less than 24 hours
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } else if (diff < 604800000) { // Less than 7 days
    return date.toLocaleDateString([], { weekday: 'short' })
  } else {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
  }
}

function formatTime(dateStr) {
  return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}
</script>

<style scoped>
.chat-page {
  display: flex;
  height: calc(100vh - 150px);
  background: #f1f5f9;
}

/* Sidebar */
.sidebar {
  width: 300px;
  background: #1e293b;
  color: white;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.sidebar-header {
  padding: 20px;
  border-bottom: 1px solid #334155;
}

.sidebar-header h2 {
  font-size: 1.1rem;
  margin-bottom: 12px;
}

.new-chat-btn {
  width: 100%;
  padding: 10px;
  background: #059669;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.2s;
}

.new-chat-btn:hover {
  background: #047857;
}

.conversations-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.conversation-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 4px;
  transition: background 0.2s;
}

.conversation-item:hover {
  background: #334155;
}

.conversation-item.active {
  background: #059669;
}

.conv-info {
  flex: 1;
  min-width: 0;
}

.conv-title {
  display: block;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.conv-date {
  font-size: 0.75rem;
  opacity: 0.7;
}

.delete-btn {
  background: none;
  border: none;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s;
}

.conversation-item:hover .delete-btn {
  opacity: 0.7;
}

.delete-btn:hover {
  opacity: 1 !important;
}

.no-conversations {
  text-align: center;
  padding: 40px 20px;
  opacity: 0.7;
}

.no-conversations .hint {
  font-size: 0.85rem;
  margin-top: 8px;
}

.sidebar-footer {
  padding: 16px;
  border-top: 1px solid #334155;
}

.credits-display {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
}

.credits-icon {
  font-size: 1.2rem;
}

/* Mobile sidebar toggle */
.sidebar-toggle {
  display: none;
  position: fixed;
  top: 10px;
  left: 10px;
  z-index: 1000;
  width: 40px;
  height: 40px;
  background: #1e293b;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.2rem;
  cursor: pointer;
}

/* Main Chat Area */
.chat-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: white;
  border-bottom: 1px solid #e2e8f0;
}

.header-info h1 {
  font-size: 1.2rem;
  font-weight: 600;
  color: #1e293b;
}

.header-subtitle {
  font-size: 0.85rem;
  color: #64748b;
}

.message-count {
  font-size: 0.85rem;
  color: #64748b;
  background: #f1f5f9;
  padding: 4px 12px;
  border-radius: 12px;
}

/* Messages Container */
.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.welcome-message {
  max-width: 500px;
  margin: 60px auto;
  text-align: center;
  padding: 40px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.welcome-icon {
  font-size: 3rem;
  margin-bottom: 16px;
}

.welcome-message h2 {
  font-size: 1.4rem;
  margin-bottom: 12px;
  color: #1e293b;
}

.welcome-message p {
  color: #64748b;
  margin-bottom: 20px;
}

.welcome-disclaimer {
  background: #fef3c7;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #92400e;
  margin-bottom: 24px;
  text-align: left;
}

.start-btn {
  padding: 12px 32px;
  background: #059669;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
}

.start-btn:hover {
  background: #047857;
}

/* Messages */
.messages-list {
  max-width: 800px;
  margin: 0 auto;
}

.message {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

.message.user {
  flex-direction: row-reverse;
}

.message-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.message.assistant .message-avatar {
  background: #dcfce7;
}

.message-content {
  max-width: 70%;
  background: white;
  padding: 12px 16px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}

.message.user .message-content {
  background: #059669;
  color: white;
}

.message-text {
  line-height: 1.5;
  font-size: 0.95rem;
}

.message-time {
  display: block;
  font-size: 0.75rem;
  opacity: 0.6;
  margin-top: 6px;
}

/* Typing indicator */
.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 8px 0;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background: #94a3b8;
  border-radius: 50%;
  animation: typing 1.4s infinite;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-6px); }
}

/* Input Area */
.input-area {
  padding: 16px 24px;
  background: white;
  border-top: 1px solid #e2e8f0;
}

.input-form {
  display: flex;
  gap: 12px;
  max-width: 800px;
  margin: 0 auto;
}

.input-form textarea {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  resize: none;
  font-size: 0.95rem;
  outline: none;
  transition: border-color 0.2s;
}

.input-form textarea:focus {
  border-color: #059669;
}

.input-form button {
  padding: 12px 24px;
  background: #059669;
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.input-form button:hover:not(:disabled) {
  background: #047857;
}

.input-form button:disabled {
  background: #94a3b8;
  cursor: not-allowed;
}

.limit-warning, .no-credits-warning {
  text-align: center;
  padding: 16px;
  background: #fef3c7;
  border-radius: 8px;
  color: #92400e;
  max-width: 800px;
  margin: 0 auto;
}

.limit-warning .new-chat-btn {
  margin-top: 12px;
  width: auto;
  padding: 8px 20px;
}

.no-credits-warning a {
  color: #059669;
  font-weight: 500;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  padding: 24px;
  border-radius: 12px;
  max-width: 400px;
  width: 90%;
}

.modal h3 {
  margin-bottom: 8px;
}

.modal p {
  color: #64748b;
  margin-bottom: 20px;
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.cancel-btn {
  padding: 8px 16px;
  background: #e2e8f0;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.delete-confirm-btn {
  padding: 8px 16px;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .sidebar {
    position: fixed;
    left: -300px;
    top: 0;
    bottom: 0;
    z-index: 999;
    transition: left 0.3s;
  }
  
  .sidebar.open {
    left: 0;
  }
  
  .sidebar-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .chat-header {
    padding-left: 60px;
  }
  
  .message-content {
    max-width: 85%;
  }
}
</style>